import { btn, field, table, text } from '../ui';
import {
  formatZoneLocalWindow,
  getBlockLabels,
  isDstAt,
  shiftDate,
  zoneColor,
  zoneEnd,
} from '../schedule.mjs';

// Same shift half a year later, i.e. the other side of the DST change.
const otherSeason = (dateStr) => shiftDate(dateStr, 183);

// Listed first in the editor; the full IANA set follows, grouped by region.
const COMMON_TIMEZONES = [
  'Asia/Kolkata', 'Asia/Seoul', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore',
  'Asia/Dubai', 'Europe/Paris', 'Europe/Berlin', 'Europe/London',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Sao_Paulo', 'Australia/Sydney', 'UTC',
];

// supportedValuesOf returns canonical IDs only, so working aliases such as
// Asia/Kolkata and UTC are absent from it. Merge the common list back in.
const OFFERED_TIMEZONES = (() => {
  let canonical = [];
  try {
    canonical = Intl.supportedValuesOf('timeZone'); // unavailable before 2022
  } catch {
    canonical = [];
  }
  return [...new Set([...COMMON_TIMEZONES, ...canonical])].sort();
})();

const TIMEZONES_BY_REGION = OFFERED_TIMEZONES.reduce((groups, tz) => {
  const region = tz.includes('/') ? tz.split('/')[0] : 'Other';
  (groups[region] = groups[region] || []).push(tz);
  return groups;
}, {});

export default function ZonesPanel({
  zones,
  orderedZones,
  blocksPerWeek,
  blocksPreview,
  onBlocksPreview,
  blockLabels,
  zoneErrors,
  roster,
  scheduleStart,
  editing,
  draft,
  setDraft,
  onEdit,
  onCancel,
  onSave,
  onUpdateDraft,
  simulating,
  onSimulate,
  onSimulateExit,
}) {
  const editable = editing || simulating;
  return (
      <div className="mb-6 border rounded p-4">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold">Zones and shifts</h2>
          {editing ? (
            <div className="flex gap-2">
              <button onClick={onSave} className={btn.save}>Save</button>
              <button onClick={onCancel} className={btn.cancel}>Cancel</button>
            </div>
          ) : simulating ? (
            <button onClick={onSimulateExit} className={btn.cancel}>Exit simulation</button>
          ) : (
            <div className="flex gap-2">
              <button onClick={onSimulate} className={btn.cancel}>Simulate</button>
              <button onClick={onEdit} className={btn.primary}>Edit zones</button>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Shift windows are set in UTC so coverage never gaps. Local hours are shown for the zone's timezone and follow DST.
        </p>

        <div className="text-sm mb-3">
          Blocks per week:{' '}
          {editable ? (
            <select
              value={blocksPreview}
              onChange={(e) => onBlocksPreview(Number(e.target.value))}
              className={field.inputInline}
            >
              {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                <option key={n} value={n}>{n} ({getBlockLabels(n).join(', ')})</option>
              ))}
            </select>
          ) : (
            <span className="font-medium">
              {blocksPerWeek} ({getBlockLabels(blocksPerWeek).join(', ')})
            </span>
          )}
        </div>

        {simulating && (
          <p className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            Simulation. Change the hours, the zones or the blocks per week and read the effect in
            Projection per zone below. Nothing is saved: the schedule still runs {blocksPerWeek}{' '}
            block{blocksPerWeek === 1 ? '' : 's'} per week on the zones above. Use Edit zones to
            make a change real.
          </p>
        )}

        {zoneErrors.length > 0 && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs">
            {zoneErrors.map((err, i) => <div key={i}>{err}</div>)}
          </div>
        )}

        {!editable ? (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs">
              <tr><th className={table.head}>Zone</th><th className={table.head}>UTC</th><th className={table.head}>Local</th><th className={table.head}>Timezone</th><th className={table.head}>People</th></tr>
            </thead>
            <tbody>
              {orderedZones.map((zone, i) => (
                <tr key={zone.name} className="border-t">
                  <td className="p-2"><span className={`px-2 py-1 rounded text-xs ${zoneColor(i)}`}>{zone.name}</span></td>
                  <td className="p-2">{zone.start} - {zoneEnd(zone)} ({zone.durationHours}h)</td>
                  <td className="p-2">{formatZoneLocalWindow(zone, scheduleStart) || '—'}</td>
                  <td className="p-2 text-xs text-gray-500">{zone.tz}</td>
                  <td className="p-2">{(roster?.[zone.name] || []).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2 items-center flex-wrap text-xs font-medium text-gray-500">
              <span className="w-24">Name</span>
              <span className="w-32">Start (UTC)</span>
              <span className="w-20">Length</span>
              <span className="w-32">Ends (UTC)</span>
              <span className="w-52">Timezone</span>
              <span className="w-44">
                Local hours
                <span
                  className="ml-1 cursor-help border border-gray-400 text-gray-500 rounded-full px-1"
                  title={
                    'Name: label shown on the schedule, e.g. IST.\n' +
                    'Start (UTC): when the shift begins. Zones are ordered by this, follow-the-sun.\n' +
                    'Length: shift length in hours. All zones must add up to 24 with no gap.\n' +
                    'Ends (UTC): derived from the start and the length.\n' +
                    'Timezone: used only to display local hours, which follow DST.\n' +
                    'Local hours: derived from the UTC window and the timezone.'
                  }
                >
                  ?
                </span>
              </span>
            </div>

            {draft.map((zone, i) => (
              <div key={i} className="flex gap-2 items-center flex-wrap">
                <input
                  value={zone.name}
                  onChange={(e) => onUpdateDraft(i, { name: e.target.value })}
                  placeholder="Name"
                  className="border rounded px-2 py-1 w-24 text-sm"
                  title="Short zone name shown on the schedule, e.g. IST"
                />
                <input
                  type="time"
                  value={zone.start}
                  onChange={(e) => onUpdateDraft(i, { start: e.target.value })}
                  title="When the shift starts, in UTC. Zones are ordered by this."
                  className={`${field.inputSm} w-32`}
                />
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={zone.durationHours}
                  onChange={(e) => onUpdateDraft(i, { durationHours: Number(e.target.value) })}
                  title="Shift length in hours. All zones together must add up to 24."
                  className="border rounded px-2 py-1 w-20 text-sm"
                />
                <span className="w-32 text-sm text-gray-600">
                  h, ends {zone.durationHours > 0 ? zoneEnd(zone) : '—'}
                </span>
                <select
                  value={zone.tz}
                  onChange={(e) => onUpdateDraft(i, { tz: e.target.value })}
                  title="Timezone used to show local hours. Does not change the UTC window."
                  className={`${field.inputSm} w-52`}
                >
                  {zone.tz && !OFFERED_TIMEZONES.includes(zone.tz) && (
                    <option value={zone.tz}>{zone.tz} (unknown)</option>
                  )}
                  <optgroup label="Common">
                    {COMMON_TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </optgroup>
                  {Object.entries(TIMEZONES_BY_REGION).map(([region, list]) => (
                    <optgroup key={region} label={region}>
                      {list.map((tz) => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <span className="w-44 text-sm">
                  {formatZoneLocalWindow(zone, scheduleStart) || (
                    <span className="text-red-600">invalid</span>
                  )}
                </span>
                <button
                  onClick={() => setDraft(draft.filter((_, j) => j !== i))}
                  title={`Remove ${zone.name} and its people`}
                  className={btn.danger}
                >
                  remove
                </button>
              </div>
            ))}
            <button
              onClick={() => setDraft([...draft, { name: '', tz: 'UTC', start: '00:00', durationHours: 6 }])}
              className={btn.link}
            >
              + Add zone
            </button>
            <p className={text.hint}>
              {simulating
                ? 'Nothing here is saved. Shifts must add up to 24h with no gap.'
                : 'Removing a zone also removes its people from the roster. Shifts must add up to 24h with no gap.'}
            </p>
          </div>
        )}
      </div>
  );
}
